import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../components/Toast.jsx';
import {
  FaUser,
  FaStore,
  FaProjectDiagram,
  FaHandshake,
  FaHeart,
  FaShoppingCart,
  FaEye,
  FaTh,
  FaList,
  FaComments,
  FaFilter,
  FaChevronUp,
  FaChevronDown,
  FaSeedling,
} from 'react-icons/fa';
import ActorAvatar from '../components/ActorAvatar.jsx';
import { useProfileSidebar } from '../contexts/ProfileSidebarContext.jsx';
import { useBottomSheet } from '../contexts/BottomSheetContext.jsx';
import CreateQuestionForm from '../components/CreateQuestionForm.jsx';
import PermissionButton from '../components/PermissionButton.jsx';
import GuestPrompt from '../components/GuestPrompt.jsx';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { usePermission } from '../hooks/usePermission';
import SearchInput from '../components/SearchInput.jsx';
import { getImageUrl } from '../utils/imageUrlHelper.js';
import { getActorStatusFromLastActive } from '../utils/actorStatusHelper.js';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
// JWT関数を直接埋め込み（Vercel対応）
const decodeJWT = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;

    const jsonPayload = decodeURIComponent(
      window
        .atob(paddedBase64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
};

const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');

  if (token) {
    try {
      const decodedToken = decodeJWT(token);
      if (!decodedToken) return null;

      // userId、user_id、idの順でチェック
      const userId =
        decodedToken.userId || decodedToken.user_id || decodedToken.id;
      return userId;
    } catch {
      return null;
    }
  }
  return null;
};

// API URL設定を直接埋め込み（Vercel対応）
const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  
  const isLocalhost = 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};
const API_URL = getApiUrl();

const QuestionForest = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMaturity, setFilterMaturity] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortField, setSortField] = useState('newest');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState('list');
  const [showFilterConditions, setShowFilterConditions] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [thresholds, setThresholds] = useState({
    maxThreshold: 50,
    stages: [],
  });

  const [selectedProfile, setSelectedProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const navigate = useNavigate();
  const { openProfile } = useProfileSidebar();
  const { openBottomSheet } = useBottomSheet();

  // 権限制御フック
  const { canCreate, permissionLevel } = usePermission();

  // 検索実行ハンドラー
  const handleSearch = () => {
    setSearchQuery(searchKeyword);
  };

  // Enterキーで検索
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 閾値データを取得（認証不要エンドポイント）
  const fetchThresholds = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/consensus/question-thresholds`
      );
      setThresholds(response.data);
    } catch (error) {
      // Network Errorは静音化（サーバー未起動時）
      if (
        !error.message?.includes('Network Error') &&
        !error.message?.includes('Failed to fetch')
      ) {
        console.error('閾値の取得に失敗しました:', error);
      }
      // デフォルト値を設定
      setThresholds({
        maxThreshold: 50,
        stages: [
          { name: 'sprouting', label: '萌芽', emoji: '🌱', threshold: 0 },
          { name: 'resonance', label: '共感', emoji: '🌿', threshold: 10 },
          { name: 'execution', label: '実行', emoji: '🌲', threshold: 25 },
          { name: 'completion', label: '完成', emoji: '🍎', threshold: 50 },
        ],
      });
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');

      let url = `${API_URL}/api/consensus/questions`;
      const params = new URLSearchParams();

      if (filterMaturity !== 'all') {
        params.append('maturity', filterMaturity);
      }

      if (filterCategory !== 'all') {
        params.append('category', filterCategory);
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      params.append('sort', sortField);
      params.append('sortDirection', sortDirection);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        setQuestions(response.data.data);
      } else if (Array.isArray(response.data)) {
        setQuestions(response.data);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      // Network Errorは静音化（サーバー未起動時）
      if (
        !error.message?.includes('Network Error') &&
        !error.message?.includes('Failed to fetch')
      ) {
        console.error('問いの取得に失敗しました:', error);
      }
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [filterMaturity, filterCategory, sortField, sortDirection, searchQuery]);

  useEffect(() => {
    fetchThresholds();
    fetchQuestions();
    loadCurrentUser();
  }, [fetchThresholds, fetchQuestions]);

  /**
   * 現在のユーザー情報を取得
   */
  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentUser(response.data);
    } catch (error) {
      // Network Errorは静音化（サーバー未起動時）
      if (
        !error.message?.includes('Network Error') &&
        !error.message?.includes('Failed to fetch')
      ) {
        console.error('現在のユーザー情報取得エラー:', error);
      }
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      problem: '🌰',
      idea: '🌿',
      creative: '🌸',
      emergency: '🚨',
      collaborative: '🤝',
    };
    return icons[category] || '🌱';
  };

  const getMaturityBadge = (maturity) => {
    const maturityConfig = {
      sprouting: {
        label: '🌱 萌芽',
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
        description: '芽吹きの段階',
      },
      resonance: {
        label: '🌿 共感',
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        description: '共感が集まる段階',
      },
      execution: {
        label: '🌲 実行',
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-200',
        description: '実行に移る段階',
      },
      completion: {
        label: '🍎 完成',
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200',
        description: '結実の段階',
      },
    };

    const config = maturityConfig[maturity] || maturityConfig['sprouting'];

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text} ${config.border} border`}
      >
        {config.label}
      </span>
    );
  };

  const getActorTypeIcon = (actorType) => {
    const icons = {
      bee: '🐝',
      ant: '🐜',
      butterfly: '🦋',
      grass: '🌿',
      tree: '🌲',
    };
    return icons[actorType] || '👤';
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return '日時不明';
    }
    try {
      return new Date(dateString).toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'Input:', dateString);
      return '日時不明';
    }
  };

  /**
   * プロフィールクリック処理
   */
  const handleProfileClick = async (event, actor) => {
    event.stopPropagation();

    const actorId = actor?.actor_id || actor?.id;
    if (!actorId) {
      console.error('アクターIDがありません:', actor);
      return;
    }

    openProfile({ id: actorId });
  };


  /**
   * 信任関係作成
   */
  const handleTrust = async (profile) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/trust`,
        {
          trusted_actor_id: profile.actorId || profile.id,
          trust_score: 5,
          comment: `${profile.username}への信任`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('信任関係を作成しました');
    } catch (error) {
      console.error('信任関係作成エラー:', error);
      toast.error('信任関係の作成に失敗しました');
    }
  };

  /**
   * チャット開始
   */
  const handleStartChat = (profile) => {
    navigate(`/personal-chat/${profile.id}`);
  };

  /**
   * 友達申請
   */
  const handleFriendRequest = async (profile) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(
        `${API_URL}/api/friends/request`,
        {
          targetUserId: profile.id,
        },
        config
      );

      toast.success('友達申請を送信しました');
    } catch (error) {
      console.error('友達申請エラー:', error);
      toast.error('友達申請に失敗しました');
    }
  };

  /**
   * アクタータイプアイコン取得
   */
  const getActorTypeIconComponent = (type) => {
    const icons = {
      user: <FaUser className="text-blue-500" />,
      store: <FaStore className="text-green-500" />,
      dao: <FaProjectDiagram className="text-purple-500" />,
      project: <FaProjectDiagram className="text-orange-500" />,
    };
    return icons[type] || <FaUser className="text-gray-500" />;
  };

  /**
   * 関係性アイコン取得
   */
  const getRelationshipIcon = (type) => {
    const icons = {
      trust: <FaHandshake className="text-green-500" />,
      empathy: <FaHeart className="text-red-500" />,
      trade: <FaShoppingCart className="text-blue-500" />,
      opposition: <FaEye className="text-gray-500" />,
    };
    return icons[type] || <FaUser />;
  };

  /**
   * カード表示用のレンダリング
   */
  const renderGridCard = (question) => {
    const handleQuestionClick = () => {
      if (permissionLevel < 2) {
        navigate('/signin');
        return;
      }
      navigate(`/questions/${question.id || question.question_log_id}`);
    };

    const isLocked = permissionLevel < 2;

    return (
      <div
        key={`grid-${question.id || question.question_log_id}`}
        className={`bg-white rounded-lg shadow-sm transition-shadow border ${
          isLocked
            ? 'opacity-60 cursor-not-allowed border-amber-300'
            : 'hover:shadow-md cursor-pointer border-gray-200'
        }`}
        onClick={handleQuestionClick}
      >
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 space-y-3 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center w-full">
              <div className="flex-1">
                <div className="flex items-start gap-2">
                  <h3 className="font-semibold text-gray-900 line-clamp-2 text-sm sm:text-base flex-1">
                    {question.title}
                  </h3>
                  {permissionLevel < 2 && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-xs whitespace-nowrap flex-shrink-0">
                      <span>🔒</span>
                      <span className="hidden sm:inline">準会員以上限定</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center mt-2 sm:mt-1 space-x-2">
                  <div
                    className="flex items-center cursor-pointer hover:bg-gray-50 rounded-lg px-1 sm:px-2 py-1 transition-colors"
                    onClick={(e) => {
                      const actorData = question.actor?.userProfile
                        ? {
                            id: question.actor.userProfile.id,
                            username: question.actor.userProfile.username,
                            display_name: question.actor.display_name,
                            iconUrl:
                              question.actor.userProfile.iconUrl ||
                              question.actor.userProfile.profile_image,
                            profile_image:
                              question.actor.userProfile.profile_image,
                            type: 'user',
                          }
                        : question.actor || {};
                      handleProfileClick(e, actorData);
                    }}
                  >
                    <div className="mr-1 sm:mr-2">
                      <ActorAvatar
                        src={
                          question.actor?.userProfile?.iconUrl ||
                          question.actor?.userProfile?.profile_image ||
                          question.actor?.profile_image_url
                        }
                        alt={
                          question.actor?.userProfile?.username ||
                          question.actor?.display_name
                        }
                        actorType="user"
                        actorStatus={getActorStatusFromLastActive(question.actor?.last_active_at)}
                        size="sm"
                      />
                    </div>
                    <span className="text-xs sm:text-sm text-gray-600 hover:text-gray-800 truncate">
                      {question.actor?.display_name ||
                        question.actor?.username ||
                        question.initiator?.name ||
                        '匿名'}
                    </span>
                  </div>
                  <div className="mb-2 sm:mb-0 sm:mr-3">
                    {getMaturityBadge(question.maturity_stage)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-gray-700 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3">
            {question.content}
          </p>

          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="flex items-center">
                <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-blue-400 rounded-full mr-1"></span>
                <span className="hidden sm:inline">
                  共感者: {question.resonance_participant_count || 0}名
                </span>
                <span className="sm:hidden">
                  👥 {question.resonance_participant_count || 0}
                </span>
              </span>
              <span className="flex items-center">
                <span
                  className={`w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full mr-1 ${
                    question.urgency_level === 'high'
                      ? 'bg-red-500'
                      : question.urgency_level === 'medium'
                        ? 'bg-yellow-400'
                        : 'bg-lime-400'
                  }`}
                ></span>
                <span className="hidden sm:inline">
                  緊急度:{' '}
                  {question.urgency_level === 'high'
                    ? '高'
                    : question.urgency_level === 'medium'
                      ? '中'
                      : '低'}
                </span>
                <span className="sm:hidden">
                  {question.urgency_level === 'high'
                    ? '🔴高'
                    : question.urgency_level === 'medium'
                      ? '🟡中'
                      : '🟢低'}
                </span>
              </span>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <span className="text-xs text-gray-500 hidden sm:inline">
                成長度:
              </span>
              <span className="text-xs text-gray-500 sm:hidden">🌱</span>
              <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    question.maturity_stage === 'sprouting'
                      ? 'bg-green-400'
                      : question.maturity_stage === 'resonance'
                        ? 'bg-blue-400'
                        : question.maturity_stage === 'execution'
                          ? 'bg-purple-400'
                          : 'bg-orange-400'
                  }`}
                  style={{
                    width: `${Math.min(((question.weighted_resonance_count || 0) / thresholds.maxThreshold) * 100, 100)}%`,
                  }}
                ></div>
              </div>
              <span className="text-xs text-gray-500">
                {Math.floor(
                  ((question.weighted_resonance_count || 0) /
                    thresholds.maxThreshold) *
                    100
                )}
                %
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-gray-500 space-y-1 sm:space-y-0">
            <span className="truncate">
              <span className="hidden sm:inline">植えた日: </span>
              <span className="sm:hidden">🌱 </span>
              {formatDate(
                question.created_at ||
                  question.startDate ||
                  question.createdAt ||
                  question.updatedAt ||
                  question.date
              )}
            </span>
            <div className="flex items-center space-x-2 flex-wrap gap-1">
              {question.context_tags && (
                <span className="px-2 py-1 bg-gray-100 rounded-full text-xs truncate max-w-24 sm:max-w-32">
                  {question.context_tags}
                </span>
              )}
              {question.project?.title && (
                <span className="flex items-center">
                  <span className="mr-1">🌱</span>
                  <span className="truncate max-w-20 sm:max-w-32">
                    {question.project.title}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * リスト表示用のレンダリング
   */
  const renderListCard = (question) => {
    const handleQuestionClick = () => {
      if (permissionLevel < 2) {
        navigate('/signin');
        return;
      }
      navigate(`/questions/${question.id || question.question_log_id}`);
    };

    const isLocked = permissionLevel < 2;

    return (
      <div
        key={`list-${question.id || question.question_log_id}`}
        className={`bg-white rounded-lg shadow-sm transition-shadow border ${
          isLocked
            ? 'opacity-60 cursor-not-allowed border-amber-300'
            : 'hover:shadow-md cursor-pointer border-gray-200'
        }`}
        onClick={handleQuestionClick}
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-start space-x-2 sm:space-x-4">
            <div
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={(e) => {
                const actorData = question.actor?.userProfile
                  ? {
                      id: question.actor.userProfile.id,
                      username: question.actor.userProfile.username,
                      display_name: question.actor.display_name,
                      iconUrl:
                        question.actor.userProfile.iconUrl ||
                        question.actor.userProfile.profile_image,
                      profile_image: question.actor.userProfile.profile_image,
                      type: 'user',
                      last_active_at: question.actor.last_active_at,
                    }
                  : question.actor || {};
                handleProfileClick(e, actorData);
              }}
            >
              <ActorAvatar
                src={
                  question.actor?.userProfile?.iconUrl ||
                  question.actor?.userProfile?.profile_image ||
                  question.actor?.profile_image_url
                }
                alt={question.actor?.display_name || '匿名'}
                actorType="user"
                actorStatus={getActorStatusFromLastActive(question.actor?.last_active_at)}
                size="md"
                showName={false}
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-xs sm:text-sm text-gray-600 truncate">
                    by{' '}
                    {question.actor?.display_name ||
                      question.actor?.username ||
                      question.initiator?.name ||
                      '匿名'}
                  </span>
                  {getMaturityBadge(question.maturity_stage)}
                </div>
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <span className="text-gray-400 hidden sm:inline">•</span>
                  <span className="truncate">
                    <span className="hidden sm:inline">植えた日: </span>
                    <span className="sm:hidden">🌱 </span>
                    {formatDate(
                      question.created_at ||
                        question.startDate ||
                        question.createdAt ||
                        question.updatedAt ||
                        question.date
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2 mb-2">
                <h3 className="font-semibold text-gray-900 line-clamp-1 text-sm sm:text-base flex-1">
                  {question.title}
                </h3>
                {permissionLevel < 2 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-xs whitespace-nowrap flex-shrink-0">
                    <span>🔒</span>
                    <span className="hidden sm:inline">準会員以上限定</span>
                  </div>
                )}
              </div>

              <p className="text-gray-700 text-xs sm:text-sm mb-3 line-clamp-1 sm:line-clamp-2">
                {question.content}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-4 text-xs sm:text-sm text-gray-600">
                  <span className="flex items-center">
                    <FaHeart className="mr-1 text-xs text-red-400" />
                    <span className="hidden sm:inline">
                      共感者: {question.resonance_participant_count || 0}名
                    </span>
                    <span className="sm:hidden">
                      {question.resonance_participant_count || 0}
                    </span>
                  </span>
                  <span className="flex items-center">
                    <FaComments className="mr-1 text-xs text-blue-400" />
                    <span className="hidden sm:inline">
                      コメント: {question.comment_count || 0}件
                    </span>
                    <span className="sm:hidden">
                      {question.comment_count || 0}
                    </span>
                  </span>
                  <span className="flex items-center">
                    <span
                      className={`w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full mr-1 ${
                        question.urgency_level === 'high'
                          ? 'bg-red-500'
                          : question.urgency_level === 'medium'
                            ? 'bg-yellow-400'
                            : 'bg-lime-400'
                      }`}
                    ></span>
                    <span className="hidden sm:inline">
                      緊急度:{' '}
                      {question.urgency_level === 'high'
                        ? '高'
                        : question.urgency_level === 'medium'
                          ? '中'
                          : '低'}
                    </span>
                    <span className="sm:hidden">
                      {question.urgency_level === 'high'
                        ? '高'
                        : question.urgency_level === 'medium'
                          ? '中'
                          : '低'}
                    </span>
                  </span>
                </div>

                <div className="flex items-center space-x-1 sm:space-x-2">
                  <span className="text-xs text-gray-500 hidden sm:inline">
                    成長度:
                  </span>
                  <span className="text-xs text-gray-500 sm:hidden">🌱</span>
                  <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        question.maturity_stage === 'sprouting'
                          ? 'bg-green-400'
                          : question.maturity_stage === 'resonance'
                            ? 'bg-blue-400'
                            : question.maturity_stage === 'execution'
                              ? 'bg-purple-400'
                              : 'bg-orange-400'
                      }`}
                      style={{
                        width: `${Math.min(((question.weighted_resonance_count || 0) / thresholds.maxThreshold) * 100, 100)}%`,
                      }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500">
                    {Math.floor(
                      ((question.weighted_resonance_count || 0) /
                        thresholds.maxThreshold) *
                        100
                    )}
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="問いの森を探索中..." />;
  }

  if (!Array.isArray(questions)) {
    return (
      <div className="bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <div className="text-4xl sm:text-6xl mb-4">🌱</div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
            データの読み込みに問題があります
          </h3>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            ページを再読み込みしてください。
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors w-full sm:w-auto min-h-[48px] sm:min-h-0"
          >
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* フィルター・検索機能（ナビゲーションバー下ビタ付け） */}
      <div
        className="sticky top-0 md:top-0 z-40 shadow-lg border-b border-gray-200 backdrop-blur-sm"
        style={{ background: 'rgba(255, 248, 220, 0.95)' }}
      >
        {/* ヘッダー */}
        <div
          className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 cursor-pointer select-none"
          onClick={() => setShowFilterConditions(!showFilterConditions)}
        >
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
              <span className="text-xs opacity-90">🌳</span>

              {/* キーワード */}
              {searchKeyword && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                  「{searchKeyword}」
                </span>
              )}

              {/* フィルター条件表示 */}
              {filterCategory !== 'all' && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                  {filterCategory === 'problem'
                    ? '🌰 課題解決'
                    : filterCategory === 'idea'
                      ? '🌿 アイデア'
                      : filterCategory === 'creative'
                        ? '🌸 創造'
                        : filterCategory === 'emergency'
                          ? '🚨 緊急'
                          : filterCategory === 'collaborative'
                            ? '🤝 協働'
                            : filterCategory}
                </span>
              )}

              {filterMaturity !== 'all' && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                  {filterMaturity === 'sprouting'
                    ? '🌱 萌芽'
                    : filterMaturity === 'resonance'
                      ? '🌿 共感'
                      : filterMaturity === 'execution'
                        ? '🌲 実行'
                        : '🍎 完成'}
                </span>
              )}

              {/* 並び順表示 */}
              {sortField !== 'newest' && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                  {sortField === 'resonance'
                    ? `💪 成長度${sortDirection === 'desc' ? '高い順' : '低い順'}`
                    : sortField === 'activity'
                      ? `⚡ 活動${sortDirection === 'desc' ? '活発順' : '静穏順'}`
                      : sortField === 'maturity'
                        ? `🍎 成熟度${sortDirection === 'desc' ? '成熟順' : '萌芽順'}`
                        : sortField === 'comments'
                          ? `💬 コメント数${sortDirection === 'desc' ? '多い順' : '少ない順'}`
                          : sortField}
                </span>
              )}

              {/* 結果件数 */}
              {questions.length > 0 && (
                <span className="text-xs opacity-75">
                  {questions.length}件の問い
                </span>
              )}
            </div>

            {/* 折りたたみアイコン */}
            <span
              className={`transition-transform duration-200 text-xs ${showFilterConditions ? 'rotate-180' : ''}`}
            >
              ▼
            </span>
          </div>
        </div>

        {/* コンテンツ */}
        {!showFilterConditions && (
          <div
            className="p-4 space-y-4"
            style={{ backgroundColor: 'rgba(101, 163, 13, 0.1)' }}
          >
            {/* 検索バー */}
            <div className="flex items-center gap-2">
              <SearchInput
                value={searchKeyword}
                onChange={setSearchKeyword}
                placeholder="問いのタイトルや内容で検索..."
                onKeyPress={handleSearchKeyPress}
                className="flex-1"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                検索
              </button>
            </div>

            {/* フィルター選択エリア - インライン配置 */}
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  成熟度:
                </span>
                <select
                  value={filterMaturity}
                  onChange={(e) => setFilterMaturity(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                >
                  <option value="all">すべて</option>
                  <option value="sprouting">🌱 萌芽</option>
                  <option value="resonance">🌿 共感</option>
                  <option value="execution">🌲 実行</option>
                  <option value="completion">🍎 完成</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  カテゴリ:
                </span>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                >
                  <option value="all">すべて</option>
                  <option value="problem">🌰 課題解決</option>
                  <option value="idea">🌿 アイデア</option>
                  <option value="creative">🌸 創造</option>
                  <option value="emergency">🚨 緊急</option>
                  <option value="collaborative">🤝 協働</option>
                </select>
              </div>

              {/* 並び順項目選択 */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  並び順:
                </span>
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                >
                  <option value="newest">🆕 新着</option>
                  <option value="resonance">💪 成長度</option>
                  <option value="activity">⚡ 活動</option>
                  <option value="maturity">🍎 成熟度</option>
                  <option value="comments">💬 コメント数</option>
                </select>
              </div>

              {/* 昇順・降順選択 */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  順序:
                </span>
                <select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  disabled={sortField === 'newest' || sortField === 'oldest'}
                >
                  <option value="desc">
                    {sortField === 'comments' ? '多い順' : 
                     sortField === 'activity' ? '活発順' : 
                     sortField === 'resonance' ? '高い順' : 
                     sortField === 'maturity' ? '成熟順' : '降順'}
                  </option>
                  <option value="asc">
                    {sortField === 'comments' ? '少ない順' : 
                     sortField === 'activity' ? '静穏順' : 
                     sortField === 'resonance' ? '低い順' : 
                     sortField === 'maturity' ? '萌芽順' : '昇順'}
                  </option>
                </select>
              </div>

              {/* 表示切り替え */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">表示:</span>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                    viewMode === 'grid'
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-gray-600 text-white border-gray-600 hover:bg-gray-700 hover:border-gray-700'
                  }`}
                >
                  <FaTh />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                    viewMode === 'list'
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-gray-600 text-white border-gray-600 hover:bg-gray-700 hover:border-gray-700'
                  }`}
                >
                  <FaList />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* コンテンツ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 問い一覧 */}
        <div
          className={
            viewMode === 'grid'
              ? 'grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
              : 'space-y-3 sm:space-y-4'
          }
        >
          {Array.isArray(questions) && questions.length > 0
            ? questions.map((question, index) => {
                const cardElement =
                  viewMode === 'grid'
                    ? renderGridCard(question)
                    : renderListCard(question);

                // リスト表示の場合、検索結果番号を追加
                if (viewMode === 'list') {
                  return (
                    <div
                      key={`list-wrapper-${question.id || question.question_log_id}`}
                      className="relative"
                    >
                      <div className="absolute -top-1 -left-1 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-sm z-10">
                        {index + 1}
                      </div>
                      {cardElement}
                    </div>
                  );
                }

                return cardElement;
              })
            : null}
        </div>

        {/* 空の状態 */}
        {!loading && Array.isArray(questions) && questions.length === 0 && (
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="text-4xl sm:text-6xl mb-4">🌱</div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              まだ問いがありません
            </h3>
            <p className="text-gray-600 mb-6 text-sm sm:text-base max-w-md mx-auto">
              最初の問いを立てて、コミュニティの対話を始めましょう。
            </p>
            <div className="text-center">
              <p className="text-gray-500 text-sm">
                右上のボタンから問いを植えてください
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 問いを植えるボタン（スクロール追従・固定位置） */}
      <div className="fixed right-6 z-50 bottom-20 md:bottom-6">
        <PermissionButton
          action="create"
          resource="question"
          onClick={() => openBottomSheet({
            content: <CreateQuestionForm onSuccess={() => {
              fetchQuestions();
            }} />,
            title: '問いを植える',
            height: 'full'
          })}
          className="bg-green-600 text-white p-3 sm:p-4 rounded-full hover:bg-green-700 transition-colors shadow-lg"
        >
          {/* モバイル版：縦配置（アイコン上・文字下） */}
          <div className="md:hidden flex flex-col items-center justify-center min-w-[45px] min-h-[45px]">
            <div className="flex items-center mb-1">
              <FaSeedling className="text-xl" />
              <span className="text-base ml-0.5">+</span>
            </div>
            <span className="text-xs font-medium">問い</span>
          </div>

          {/* PC版：横並び（元仕様） */}
          <div className="hidden md:flex items-center gap-2">
            <FaSeedling className="text-xl" />
            <span className="text-sm">問いを植える</span>
          </div>
        </PermissionButton>
      </div>

    </div>
  );
};

// ErrorBoundaryでラップしたコンポーネントをエクスポート
const QuestionForestWithErrorBoundary = () => {
  return (
    <ErrorBoundary>
      <QuestionForest />
    </ErrorBoundary>
  );
};

export default QuestionForestWithErrorBoundary;
